import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {StudyOverviewViewQuery} from 'studies/workspace/overview/__generated/StudyOverviewViewQuery';
import StudyAnalysisListView from 'studies/workspace/overview/StudyAnalysisListView';
import Pane, {PaneBorder} from 'ui-library/pane/Pane';

import MultiProcedureRunAndPlanListingTable from './MultiProcedureRunAndPlanListingTable';
import {ProcedureRunTab, StudyOverviewContext} from './MultiProcedureRunAndPlanOverviewTabContents';
import ProcedureRunDetailsView from './ProcedureRunDetailsView';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;

export default function MultiProcedureRunAndPlanOverview({study}: {study: StudyNode}): JSX.Element {
  assert(
    BenchlingConfig.STUDIES_ENABLE_PROCEDURE_RUN_DESIGN,
    'Expected STUDIES_ENABLE_PROCEDURE_RUN_DESIGN to be true'
  );

  const {selectedProcedureRun, selectedProcedureRunPlan, currentProcedureRunTab, setCurrentProcedureRunTab} =
    StudyOverviewContext.useContext();
  return (
    <div
      className={css(
        currentProcedureRunTab === ProcedureRunTab.OVERVIEW
          ? styles.studyBackgroundGray
          : styles.studyBackgroundWhite
      )}
    >
      {selectedProcedureRun.id == null && selectedProcedureRunPlan.id == null ? (
        <div className={css(styles.runsContainer)}>
          <MultiProcedureRunAndPlanListingTable studyId={study.id} />
          <div className={css(styles.analysisPaneWrapper)}>
            <Pane
              title="Analyses"
              paneBorder={PaneBorder.SOLID}
              dataTestAttrs={{component: 'MultiProcedureRunOverview', element: 'analysesPane'}}
            >
              <div className={css(styles.analysisContainer)}>
                <StudyAnalysisListView studyId={study.id} showDate={false} />
              </div>
            </Pane>
          </div>
        </div>
      ) : (
        <ProcedureRunDetailsView
          studyType={study.studySchema.studyType}
          studyId={study.id}
          selectedProcedureRun={selectedProcedureRun}
          currentProcedureRunTab={currentProcedureRunTab}
          setCurrentProcedureRunTab={setCurrentProcedureRunTab}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  studyBackgroundGray: {
    backgroundColor: Color.GRAY_100,
    height: '100%',
    maxHeight: '100%',
  },
  studyBackgroundWhite: {
    backgroundColor: Color.WHITE,
    height: '100%',
    maxHeight: '100%',
  },
  runsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '8px',
    height: '100%',
    maxHeight: '100%',
  },
  analysisPaneWrapper: {
    flex: '0 0 auto',
  },
  analysisContainer: {
    padding: '12px',
  },
});
